import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { TablePagination } from '../../components/ui/table-pagination';
import { toast } from 'sonner';
import { 
  ArrowLeft, Tag, Plus, Pencil, Trash2, Search
} from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function AdminPromoCodes() {
  const navigate = useNavigate();
  const [promoCodes, setPromoCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(20);
  
  const [form, setForm] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 10,
    min_order_value: null,
    max_discount: null,
    usage_limit: null,
    per_user_limit: 1,
    valid_from: new Date().toISOString().split('T')[0],
    valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    applicable_categories: null,
    is_active: true
  });

  const fetchPromoCodes = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(`${API}/admin/promo-codes?${params}`);
      setPromoCodes(response.data.promo_codes || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Failed to fetch promo codes:', error);
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, statusFilter, searchTerm]);

  useEffect(() => {
    fetchPromoCodes();
  }, [fetchPromoCodes]);

  // Debounced search - reset to page 1 when search term changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page !== 1) setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, page]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...form,
        valid_from: new Date(form.valid_from).toISOString(),
        valid_until: new Date(form.valid_until).toISOString()
      };
      
      if (editingCode) {
        await axios.put(`${API}/admin/promo-codes/${editingCode.id}`, data);
        toast.success('Promo code updated!');
      } else {
        await axios.post(`${API}/admin/promo-codes`, data);
        toast.success('Promo code created!');
      }
      
      setShowModal(false);
      setEditingCode(null);
      resetForm();
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save promo code');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) return;
    
    try {
      await axios.delete(`${API}/admin/promo-codes/${id}`);
      toast.success('Promo code deleted!');
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to delete promo code');
    }
  };

  const handleEdit = (code) => {
    setEditingCode(code);
    setForm({
      code: code.code,
      description: code.description,
      discount_type: code.discount_type,
      discount_value: code.discount_value,
      min_order_value: code.min_order_value,
      max_discount: code.max_discount,
      usage_limit: code.usage_limit,
      per_user_limit: code.per_user_limit,
      valid_from: code.valid_from.split('T')[0],
      valid_until: code.valid_until.split('T')[0],
      applicable_categories: code.applicable_categories,
      is_active: code.is_active
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setForm({
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: 10,
      min_order_value: null,
      max_discount: null,
      usage_limit: null,
      per_user_limit: 1,
      valid_from: new Date().toISOString().split('T')[0],
      valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      applicable_categories: null,
      is_active: true
    });
  };

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <AdminSidebar />
      <AdminMobileNav />

      {/* Main Content */}
      <main className="flex-1 p-4 md:p-8 md:ml-64 pt-16 md:pt-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/admin')}
              className="w-10 h-10 rounded-full hover:bg-stone-200 flex items-center justify-center md:hidden"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-[Poppins]">Promo Codes</h1>
              <p className="text-slate-500">Manage discount codes and promotions</p>
            </div>
          </div>
          <Button
            onClick={() => {
              setEditingCode(null);
              resetForm();
              setShowModal(true);
            }}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            data-testid="create-promo-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Promo
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search promo codes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11"
                data-testid="promo-search"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="h-11 px-3 border border-stone-200 rounded-lg text-sm"
              data-testid="status-filter"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>

        {/* Promo Codes List */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
            </div>
          ) : promoCodes.length === 0 ? (
            <div className="p-12 text-center">
              <Tag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No promo codes found</p>
              <Button
                onClick={() => setShowModal(true)}
                className="mt-4 bg-slate-900 hover:bg-slate-800"
              >
                Create your first promo code
              </Button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-50 border-b">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Code</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Discount</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Usage</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Valid Period</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Status</th>
                      <th className="text-right px-6 py-4 text-sm font-medium text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {promoCodes.map((code) => {
                      const isExpired = new Date(code.valid_until) < new Date();
                      const isActive = code.is_active && !isExpired;
                      
                      return (
                        <tr key={code.id} className="hover:bg-stone-50" data-testid={`promo-row-${code.code}`}>
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-mono font-bold text-slate-900">{code.code}</p>
                              <p className="text-xs text-slate-500">{code.description}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-lg text-sm font-medium">
                              {code.discount_type === 'percentage' 
                                ? `${code.discount_value}% off`
                                : `₱${code.discount_value} off`
                              }
                            </span>
                            {code.max_discount && (
                              <p className="text-xs text-slate-500 mt-1">Max ₱{code.max_discount}</p>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-slate-700">
                              {code.usage_count} / {code.usage_limit || '∞'}
                            </p>
                            <p className="text-xs text-slate-500">{code.per_user_limit}/user</p>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-slate-700">
                              {new Date(code.valid_from).toLocaleDateString()}
                            </p>
                            <p className="text-xs text-slate-500">
                              to {new Date(code.valid_until).toLocaleDateString()}
                            </p>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              isActive 
                                ? 'bg-green-100 text-green-700' 
                                : isExpired 
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-stone-100 text-stone-500'
                            }`}>
                              {isExpired ? 'Expired' : isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => handleEdit(code)}
                                className="p-2 hover:bg-stone-100 rounded-lg"
                                data-testid={`edit-promo-${code.code}`}
                              >
                                <Pencil className="w-4 h-4 text-slate-600" />
                              </button>
                              <button
                                onClick={() => handleDelete(code.id)}
                                className="p-2 hover:bg-red-50 rounded-lg"
                                data-testid={`delete-promo-${code.code}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <TablePagination
                page={page}
                totalPages={totalPages}
                total={total}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={setLimit}
              />
            </>
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold text-slate-900 font-[Poppins]">
                  {editingCode ? 'Edit Promo Code' : 'Create Promo Code'}
                </h2>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <Label>Code *</Label>
                  <Input
                    value={form.code}
                    onChange={(e) => setForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                    placeholder="SUMMER2025"
                    className="h-12 mt-1.5 font-mono"
                    required
                    data-testid="promo-code-input"
                  />
                </div>
                
                <div>
                  <Label>Description *</Label>
                  <Input
                    value={form.description}
                    onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Summer discount for all services"
                    className="h-12 mt-1.5"
                    required
                    data-testid="promo-description-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Discount Type</Label>
                    <select
                      value={form.discount_type}
                      onChange={(e) => setForm(prev => ({ ...prev, discount_type: e.target.value }))}
                      className="w-full h-12 mt-1.5 border border-stone-200 rounded-lg px-3"
                      data-testid="promo-type-select"
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount (₱)</option>
                    </select>
                  </div>
                  <div>
                    <Label>Discount Value *</Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.discount_value}
                      onChange={(e) => setForm(prev => ({ ...prev, discount_value: parseFloat(e.target.value) || 0 }))}
                      className="h-12 mt-1.5"
                      required
                      data-testid="promo-value-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Min Order Value</Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.min_order_value || ''}
                      onChange={(e) => setForm(prev => ({ ...prev, min_order_value: e.target.value ? parseFloat(e.target.value) : null }))}
                      placeholder="Optional"
                      className="h-12 mt-1.5"
                      data-testid="promo-min-order"
                    />
                  </div>
                  <div>
                    <Label>Max Discount</Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.max_discount || ''}
                      onChange={(e) => setForm(prev => ({ ...prev, max_discount: e.target.value ? parseFloat(e.target.value) : null }))}
                      placeholder="Optional"
                      className="h-12 mt-1.5"
                      data-testid="promo-max-discount"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Usage Limit (Total)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.usage_limit || ''}
                      onChange={(e) => setForm(prev => ({ ...prev, usage_limit: e.target.value ? parseInt(e.target.value) : null }))}
                      placeholder="Unlimited"
                      className="h-12 mt-1.5"
                      data-testid="promo-usage-limit"
                    />
                  </div>
                  <div>
                    <Label>Per User Limit</Label>
                    <Input
                      type="number"
                      min="1"
                      value={form.per_user_limit}
                      onChange={(e) => setForm(prev => ({ ...prev, per_user_limit: parseInt(e.target.value) || 1 }))}
                      className="h-12 mt-1.5"
                      data-testid="promo-per-user"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Valid From *</Label>
                    <Input
                      type="date"
                      value={form.valid_from}
                      onChange={(e) => setForm(prev => ({ ...prev, valid_from: e.target.value }))}
                      className="h-12 mt-1.5"
                      required
                      data-testid="promo-valid-from"
                    />
                  </div>
                  <div>
                    <Label>Valid Until *</Label>
                    <Input
                      type="date"
                      value={form.valid_until}
                      onChange={(e) => setForm(prev => ({ ...prev, valid_until: e.target.value }))}
                      className="h-12 mt-1.5"
                      required
                      data-testid="promo-valid-until"
                    />
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-4 bg-stone-50 rounded-xl">
                  <Switch
                    checked={form.is_active}
                    onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_active: checked }))}
                    data-testid="promo-active-toggle"
                  />
                  <div>
                    <p className="font-medium text-slate-700">Active</p>
                    <p className="text-xs text-slate-500">Enable this promo code for customers</p>
                  </div>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowModal(false);
                      setEditingCode(null);
                      resetForm();
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-slate-900 hover:bg-slate-800"
                    data-testid="save-promo-btn"
                  >
                    {editingCode ? 'Update' : 'Create'} Promo Code
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
